import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * `<shading-joystick>` — broadcast shading monoknob lever-knob assembly,
 * rendered top-down.
 *
 * The industry calls this control a joystick. Mechanically it is a
 * **lever with pivot**: the shaft is rigid and pivots around a
 * horizontal axle inside the mounting frame, below the chassis surface.
 * The cap sits above the chassis at the top end of the lever; the
 * bottom of the shaft drives the iris potentiometer through the longer
 * arm of the lever.
 *
 * The operator drives three things with a single hand:
 *
 *   - **Forward / backward throw** of the lever rotates it about the
 *     pivot, lifting or pushing the cap forward or back. Drives iris
 *     on shading panels (OCP-400, CGP-500, RCP-3500). Because the
 *     pivot is below the chassis, cap motion in the operator's plane
 *     amplifies the shaft motion at chassis level by the lever ratio
 *     (cap-arm / chassis-arm ≈ 2.1 : 1 on the OCP-400).
 *   - **Rotation collar** at the cap's base. Drives master black on
 *     the OCP-400; master black + a second assignable parameter on the
 *     CGP-500 (`hasSecondaryRing`).
 *   - **Axial press** on the cap top. Emits the same Concept.Preview
 *     as the panel's dedicated PREVIEW button. Press is triggered by
 *     a tap (touch / no-drag click) or by a right-mouse click — the
 *     latter mirrors the typical broadcast-panel idiom of having a
 *     dedicated press gesture distinct from the lever motion gesture.
 *
 * # Separation from the chassis recess
 *
 * The visible rectangular chassis recess (the "joystick lip" — the
 * rounded-corner well in the lower chassis that frames the lever
 * assembly) is rendered as a separate `chassis-feature` entry in the
 * panel inventory, NOT as part of this primitive. This primitive
 * renders ONLY the lever-knob (cap + collar + tally) that sits inside
 * the lip. The lip is drawn behind this primitive by virtue of its
 * earlier ordering in the panel's controls array.
 *
 * # Documented hardware reference
 *
 * Maintenance Manual V12.0 (Grass Valley 3922 496 30261, 2024-02-22)
 * page 19 is the authoritative engineering line-drawing of the
 * `ASSY MONOKNOB WIRED 500-00080-01` joystick assembly. The drawing
 * shows the lever pivot, the spring tensioner (Torx-10 adjustment
 * documented in User's Guide §1.5.5), the mounting frame, and the
 * shaft → potentiometer travel.
 *
 * Pixel-derived dimensions (see `panels/ocp-400/research/joystick-
 * measurements.md` for full method and uncertainty notes):
 *
 *   - Cap-tip protrusion above chassis: **58.8 mm** (User's Guide
 *     §5.2 Figure 5-1 — joystick protrusion = 141.8 mm total panel
 *     depth − 83.0 mm chassis-only depth). DIRECT dimension.
 *   - Cap+collar diameter at equator: **33.8 mm** (front-elevation
 *     product photo anchored at the 82 mm panel width).
 *   - Pivot depth below chassis surface: **~28 mm** (CAD visual
 *     reading, ±5 mm uncertainty).
 *   - Lever-arm ratio: **~2.1 : 1** — cap-tip travels ~2× the shaft-
 *     at-chassis displacement.
 *
 * # Coordinate system
 *
 * The viewBox is 100 × 237, preserving the aspect ratio of the
 * canonical joystick host bounding box (297 × 704 vbu = 29.7 × 70.4
 * mm at the inventory's 10 vbu/mm scale). One viewBox unit therefore
 * represents 0.297 mm of chassis-space.
 *
 *   capR = 57       → cap diameter 33.8 mm at 0.297 mm/viewBox-unit
 *   collarR = 62    → collar adds ~5 vbu (1.5 mm) ring at cap base
 *   throwDisp = 70  → ±70 viewBox-units = ±20.8 mm cap displacement
 *                     at ~±14° lever throw. With the lever-arm
 *                     ratio (cap-arm 86.8 mm), the cap edge clears
 *                     the OCP-400 joystick-lip top and bottom edges
 *                     by ≈4.5 mm at full throw — the cap visibly
 *                     overshoots the chassis recess (pivot-style
 *                     lever doctrine), but more conservatively than
 *                     a deep reach so the motion still reads as
 *                     mechanically constrained.
 *
 * See `panels/ocp-400/research/joystick-measurements.md` for the full
 * pixel-derivation method and uncertainty caveats, and ADR-0005
 * (Joystick API design) for the property/event contract.
 */
@customElement('shading-joystick')
export class ShadingJoystickElement extends LitElement {
  static override styles = [
    css`
      :host {
        display: inline-block;
        /* contain: layout only — NOT paint. Paint-containment would
         * clip the cap dome when it overhangs the host bbox at
         * extreme throw, and the dome legitimately spills past the
         * joystick area's sides (cap diameter 33.8 mm is wider than
         * the joystick host bbox of 29.7 mm). */
        contain: layout;
        inline-size: 100%;
        block-size: 100%;

        /* External positionStyle sets position:absolute on the host;
         * this z-index lifts the lever-knob above its DOM-later
         * siblings (PREVIEW button etc.) so the cap dome remains
         * visible when it overhangs into adjacent panel areas at
         * extreme throws. Without this, PREVIEW renders on top of
         * the cap at min-throw. The joystick-lip chassis-feature
         * renders BEFORE this element in DOM order so it sits
         * naturally below the cap (no z-index conflict there). */
        position: relative;
        z-index: 1;

        --sj-collar: var(--control-collar-primary, #4c4c50);
        --sj-collar-secondary: var(--control-collar-secondary, #6b3aa5);
        --sj-collar-tertiary: var(--control-collar-tertiary, var(--sj-collar-secondary));
        --sj-cap: var(--control-cap, #2a2a2c);
        --sj-cap-highlight: var(--control-cap-highlight, color-mix(in oklab, var(--sj-cap) 40%, white));
        --sj-detent: var(--control-detent-line, #0a0a0b);
        --sj-pointer: var(--control-pointer, #d8d8da);
        --sj-tally-off: var(--tally-off, color-mix(in oklab, var(--sj-cap) 65%, black));
        --sj-tally-program: var(--illumination-program-on, #cc1f1f);
        --sj-tally-preview: var(--illumination-preview-on, #3a8aff);
        --sj-tally-iso: var(--illumination-iso-on, #e8b015);
        --sj-tally-idle: var(--illumination-function-on, #3a8aff);
      }

      svg {
        display: block;
        inline-size: 100%;
        block-size: 100%;
        overflow: visible;
      }

      .lever-knob {
        /* Whole lever assembly (cap + collar + master-black tick).
         * Pointer handlers attached here so drag and right-click
         * both target the full assembly, and the press animation
         * moves cap + collar together as one unit. */
        transition: transform 90ms ease-out;
        cursor: grab;
        touch-action: none;
        -webkit-touch-callout: none;
      }

      .lever-knob:active {
        cursor: grabbing;
      }

      :host([pressed]) .lever-knob {
        /* Whole-assembly axial-press feedback: translate down + shrink
         * uniformly so the assembly reads as "the entire lever sank a
         * few millimetres into the chassis", not as the cap glowing or
         * morphing. No filter — broadcast hardware doesn't change
         * brightness when pressed. */
        transform: translateY(3.5%) scale(0.93);
      }

      /* Tally-ring fill flows through CSS so var() resolves on both
       * Blink and WebKit. The state attribute on the host selects
       * which illumination token the ring uses. */
      .tally-ring {
        fill: var(--sj-tally-off);
        transition: fill 120ms ease-out;
      }
      .tally-glow {
        fill: var(--sj-tally-off);
        opacity: 0;
        filter: url(#sj-tally-glow);
        transition: opacity 120ms ease-out;
      }

      :host([tally-state='program']) .tally-ring { fill: var(--sj-tally-program); }
      :host([tally-state='program']) .tally-glow { fill: var(--sj-tally-program); opacity: 0.7; }
      :host([tally-state='preview']) .tally-ring { fill: var(--sj-tally-preview); }
      :host([tally-state='preview']) .tally-glow { fill: var(--sj-tally-preview); opacity: 0.6; }
      :host([tally-state='iso']) .tally-ring { fill: var(--sj-tally-iso); }
      :host([tally-state='iso']) .tally-glow { fill: var(--sj-tally-iso); opacity: 0.6; }
      :host([tally-state='comms']) .tally-ring { fill: var(--sj-tally-idle); }
      :host([tally-state='comms']) .tally-glow { fill: var(--sj-tally-idle); opacity: 0.45; }
      :host([tally-state='idle']) .tally-ring { fill: var(--sj-tally-idle); }
      :host([tally-state='idle']) .tally-glow { fill: var(--sj-tally-idle); opacity: 0.5; }
    `,
  ];

  /** Invert the iris throw axis (1 → forward/up vs 1 → back/down). */
  @property({ type: Boolean, reflect: true }) accessor invertVertical = false;

  /** Number of tactile detents on the primary collar (rendered as tick marks). */
  @property({ type: Number, attribute: 'primary-ring-detents' })
  accessor primaryRingDetents = 0;

  /** Number of tactile detents on the secondary collar (CGP-500). */
  @property({ type: Number, attribute: 'secondary-ring-detents' })
  accessor secondaryRingDetents = 0;

  /**
   * Whether the panel exposes the secondary collar (CGP-500 only).
   *
   * Explicit kebab-case attribute name (`has-secondary-ring`) so
   * panel compositions can bind via `?has-secondary-ring=${true}` in
   * lit-html templates — Lit's default attribute resolution would
   * otherwise look for `hassecondaryring` and silently drop the
   * binding.
   */
  @property({ type: Boolean, reflect: true, attribute: 'has-secondary-ring' })
  accessor hasSecondaryRing = false;

  /**
   * Whether the panel exposes a tertiary collar (CGP-500 — the
   * upper purple Master Gain ring that sits between the cap and the
   * primary Master-Black collar). When set, the joystick draws a
   * thin coloured ring just outside the cap edge — the visible
   * inner-most annulus of the four-ring stack.
   */
  @property({ type: Boolean, reflect: true, attribute: 'has-tertiary-ring' })
  accessor hasTertiaryRing = false;

  /** Number of tactile detents on the tertiary collar (CGP-500). */
  @property({ type: Number, attribute: 'tertiary-ring-detents' })
  accessor tertiaryRingDetents = 0;

  /**
   * Whether to render the illuminated tally ring at the base of the
   * monoknob. CGP-500 hardware: blue (idle), red (program), yellow
   * (ISO). The colour selection is driven by `tallyState`.
   */
  @property({ type: Boolean, reflect: true, attribute: 'has-tally-ring' })
  accessor hasTallyRing = false;

  /**
   * Canonical tally state for the joystick base ring. Off renders no
   * visible ring; the other states light it in the matching colour
   * from the vendor's illumination tokens. Mirrors the
   * `<tally-lamp>` primitive's state surface.
   */
  @property({ type: String, reflect: true, attribute: 'tally-state' })
  accessor tallyState: 'off' | 'program' | 'preview' | 'iso' | 'comms' | 'idle' = 'off';

  /** Tally ring around the cap (program / on-air indicator). */
  @property({ type: Boolean, reflect: true }) accessor tallyOn = false;

  /**
   * Visual press latch — drives the cap depress animation. The
   * primitive sets this attribute briefly when a press gesture
   * (tap or right-click) is detected; the host panel may also set
   * it programmatically to indicate an external preview state.
   */
  @property({ type: Boolean, reflect: true }) accessor pressed = false;

  /** Pointer-tap tolerance in viewBox units. */
  @property({ type: Number }) accessor pressTolerance = 6;

  /**
   * Master-black drag deadband in viewBox units. Horizontal pointer
   * motion within ±`masterBlackDeadbandVbu` of the drag start does NOT
   * change masterBlack; only motion BEYOND the deadband counts. This
   * is the "safe zone" that prevents the sensitive master-black
   * parameter from drifting when the operator only meant to drag
   * vertically (iris). PROVISIONAL DEFAULT: 30 vbu — roughly 9 mm of
   * pointer travel before master-black engages, which is the
   * smallest deadband at which accidental nudges during a vertical
   * iris-drag stop registering.
   */
  @property({ type: Number }) accessor masterBlackDeadbandVbu = 30;

  /**
   * Master-black drag sweep in viewBox units. The number of viewBox-x
   * units of horizontal pointer motion (BEYOND the deadband) that
   * correspond to a full 0..1 sweep of masterBlack. A larger value
   * means LESS sensitive — the operator has to drag further across
   * the screen to make a comparable change. Master black is rarely
   * touched in production and is sensitive when it is, so the default
   * is deliberately conservative so accidental small horizontal
   * motion barely registers. PROVISIONAL DEFAULT: 400 vbu (4× less
   * sensitive than the previous 100 vbu/sweep).
   */
  @property({ type: Number }) accessor masterBlackSweepVbu = 400;

  /**
   * Iris throw position, normalised 0..1.
   *   0   = lever pushed back / iris closed
   *   1   = lever pulled forward / iris open
   *   0.5 = mid-throw (default initial value)
   *
   * The shading joystick on Sony RCP and Grass Valley OCP panels is a
   * stay-put potentiometer — the lever holds whatever position the
   * operator left it at (Sony: friction-hold; Grass Valley: Torx-10
   * adjustable friction). No spring-return; this property simply
   * records the current value, with 0.5 as the default initial
   * value at primitive instantiation.
   *
   * With `invertVertical`, 0 maps to forward and 1 maps to back — for
   * panels where the operator's pull-toward opens the iris.
   */
  @property({ type: Number }) accessor throwPosition = 0.5;

  /**
   * Master black level driven by the rotary collar. Normalised 0..1
   * where 0.5 is the mid-rotation default initial value. A horizontal
   * pointer drag on the cap rotates the collar and updates this
   * value, emitting a `stick.master-black-change` event. The collar
   * (like the iris lever) is a stay-put potentiometer.
   */
  @property({ type: Number }) accessor masterBlack = 0.5;

  /**
   * Drag sensitivity — viewBox units of vertical pointer motion that
   * correspond to a full 0..1 iris sweep.
   *
   * PROVISIONAL DEFAULT: 125. Picked qualitatively so a comfortable
   * thumb drag across the joystick's visible vertical extent sweeps
   * the iris from open to closed. NOT derived from the iris-pot pulse
   * spec or operator-feel data. The IRIS CAL menu (User's Guide
   * §3.3.5) handles the digital 0..99 mapping in production; this
   * value only governs the GUI drag-to-throw curve.
   */
  @property({ type: Number }) accessor dragSweepViewBox = 125;

  // Pointer state for drag (iris + master-black) and press (preview).
  // Kept off the reactive surface so an in-flight drag does not
  // re-render every move; the throwPosition / masterBlack property
  // changes that the drag emits drive the redraw.
  //
  // Drag and press CAN coexist on the same pointer: a press-and-hold
  // (right mouse button) keeps the `pressed` attribute active while
  // pointer motion still drives iris/blacks. On a mouse both buttons
  // share a single pointerId, so the buttons-held flags below
  // disambiguate which gesture(s) are active without splitting the
  // drag reference frame.
  #pointerId = -1;
  #dragStartClientX = 0;
  #dragStartClientY = 0;
  #dragStartThrow = 0;
  #dragStartMasterBlack = 0;
  #dragMoved = false;
  #leftHeld = false;
  #rightHeld = false;

  #onPointerDown = (event: PointerEvent) => {
    // First press on a fresh pointer: seed the drag reference frame.
    // Drag-start state stays valid for the lifetime of the pointer —
    // both mouse buttons share one pointerId, so pressing a second
    // button mid-gesture does NOT reset the drag origin (which would
    // otherwise yank the iris value as the operator engages press).
    if (this.#pointerId !== event.pointerId) {
      this.#pointerId = event.pointerId;
      this.#dragStartClientX = event.clientX;
      this.#dragStartClientY = event.clientY;
      this.#dragStartThrow = this.throwPosition;
      this.#dragStartMasterBlack = this.masterBlack;
      this.#dragMoved = false;
      (event.currentTarget as Element).setPointerCapture(event.pointerId);
    }
    if (event.button === 0) {
      this.#leftHeld = true;
    }
    if (event.button === 2) {
      // Right button: axial press / preview gesture. `pressed` stays
      // true while the button is held. Crucially, drag-iris and
      // drag-master-black remain active simultaneously so the
      // operator can press-and-tilt (the real hardware allows this —
      // pushing the cap axially does not lock the lever's pivot).
      this.#rightHeld = true;
      this.pressed = true;
      this.dispatchEvent(new CustomEvent('stick.preview-trigger', {
        bubbles: true,
        composed: true,
      }));
    }
    event.preventDefault();
  };

  #onPointerMove = (event: PointerEvent) => {
    if (event.pointerId !== this.#pointerId) return;
    const dx = event.clientX - this.#dragStartClientX;
    const dy = event.clientY - this.#dragStartClientY;
    if (!this.#dragMoved && Math.hypot(dx, dy) < 2) return;
    this.#dragMoved = true;
    // Map pixel motion to viewBox units via the host's rendered size.
    const rect = this.getBoundingClientRect();
    const pxPerViewBoxY = rect.height / 237;
    const pxPerViewBoxX = rect.width / 100;
    // Vertical drag: up → throw position toward 1.0 (iris open).
    // Iris uses full vertical sensitivity (dragSweepViewBox) — this
    // is the primary parameter the operator is dragging for.
    const dThrow = -(dy / pxPerViewBoxY) / this.dragSweepViewBox;
    const newThrow = Math.max(0, Math.min(1, this.#dragStartThrow + dThrow));
    if (newThrow !== this.throwPosition) {
      this.throwPosition = newThrow;
      this.dispatchEvent(new CustomEvent('stick.iris-change', {
        detail: { value: newThrow },
        bubbles: true,
        composed: true,
      }));
    }
    // Horizontal drag: master-black with a deadband + reduced
    // sensitivity. Pointer motion within ±masterBlackDeadbandVbu of
    // the drag start does NOT change masterBlack; only motion BEYOND
    // the deadband counts. This is the "safe zone" that lets the
    // operator drag vertically (iris) without accidentally nudging
    // the sensitive master-black parameter when their hand isn't
    // perfectly steady.
    const dxVbu = dx / pxPerViewBoxX;
    const absDx = Math.abs(dxVbu);
    if (absDx > this.masterBlackDeadbandVbu) {
      const dxBeyondDeadband =
        Math.sign(dxVbu) * (absDx - this.masterBlackDeadbandVbu);
      const dMasterBlack = dxBeyondDeadband / this.masterBlackSweepVbu;
      const newMb = Math.max(0, Math.min(1, this.#dragStartMasterBlack + dMasterBlack));
      if (newMb !== this.masterBlack) {
        this.masterBlack = newMb;
        this.dispatchEvent(new CustomEvent('stick.master-black-change', {
          detail: { value: newMb },
          bubbles: true,
          composed: true,
        }));
      }
    }
  };

  #onPointerUp = (event: PointerEvent) => {
    if (event.pointerId !== this.#pointerId) return;
    if (event.button === 0) {
      this.#leftHeld = false;
    }
    if (event.button === 2) {
      this.#rightHeld = false;
      this.pressed = false;
    }
    // Pointer is fully released only when every button is up. Until
    // then we keep the drag reference frame so the still-held button
    // can continue driving iris / master-black without a discontinuity.
    if (!this.#leftHeld && !this.#rightHeld) {
      const target = event.currentTarget as Element;
      if (target.hasPointerCapture(event.pointerId)) {
        target.releasePointerCapture(event.pointerId);
      }
      this.#pointerId = -1;
      this.#dragMoved = false;
    }
    // NOTE: tap-without-drag does NOT emit preview. The press gesture
    // is bound to the right mouse button (and the dedicated PREVIEW
    // soft-button on the panel). A bare left-click — even a very
    // short one — is always "I am about to drag" and never an axial
    // press.
  };

  /**
   * Suppress the browser's context menu on the lever-knob so the
   * right-button-down gesture is exclusively ours. The actual press
   * + preview emission happens in #onPointerDown when button=2.
   */
  #onContextMenu = (event: MouseEvent) => {
    event.preventDefault();
  };

  override render() {
    // === GEOMETRY (see jsdoc and joystick-measurements.md) ===
    const capR = 57;
    const collarR = 62;
    // ±70 viewBox-units of cap displacement = ±20.8 mm at the
    // primitive's 0.297 mm/vbu scale. Combined with the cap-arm
    // length (pivot 28 mm below chassis + cap protrusion 58.8 mm =
    // 86.8 mm), this corresponds to ~±14° lever throw. At full
    // throw the cap edge clears the OCP-400 joystick-lip top and
    // bottom edges by ≈4.5 mm — the cap+collar visibly overshoot
    // the chassis recess (pivot-style lever doctrine), but more
    // conservatively than a deep reach.
    const throwDisp = 70;
    // Cap centre at neutral throw — offset +17 viewBox-units below
    // the primitive bbox midpoint (118.5) so the cap centres on the
    // joystick-lip centre rather than on the joystick host bbox
    // centre. The lip in the OCP-400 inventory sits 50 chassis-vbu
    // (= 17 viewBox-units at the 704/237 chassis-to-viewBox ratio)
    // lower than the host bbox centre, so without this offset the
    // forward overshoot would be larger than the back overshoot.
    // 118.5 + 17 = 135.5 keeps the throw symmetric about the lip.
    const capCx = 50;
    const capCyNeutral = 135.5;

    const effectiveThrow = this.invertVertical ? 1 - this.throwPosition : this.throwPosition;
    const capCy = capCyNeutral - (effectiveThrow - 0.5) * 2 * throwDisp;

    // Knurl tick marks on the primary collar. Rendered as a small arc
    // of dashes evenly spaced around the visible collar annulus.
    // Limited to 32 detents — beyond that the marks blur together at
    // panel scale.
    const detentCount = Math.min(this.primaryRingDetents, 32);

    // Master-black rotation indicator. A short tick on the cap top
    // that rotates with the masterBlack value. The collar is a
    // stay-put rotary; at masterBlack=0.5 the tick points straight
    // forward (toward smaller y / FREE-button row).
    const masterBlackAngleDeg = (this.masterBlack - 0.5) * 270;

    return html`
      <svg viewBox="0 0 100 237" preserveAspectRatio="xMidYMid meet" role="slider" aria-label="shading joystick">
        <title>Shading joystick</title>
        <desc>
          Three-in-one shading control rendered top-down. The cap pivots
          forward and back to drive iris; the knurled collar at the cap
          base rotates to drive master black; right-clicking the cap
          previews the connected camera signal (same Concept.Preview as
          the dedicated PREVIEW button).
        </desc>

        <defs>
          <radialGradient id="sj-collar-grad" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0.55" stop-color="color-mix(in oklab, var(--sj-collar) 70%, black)" />
            <stop offset="0.85" stop-color="var(--sj-collar)" />
            <stop offset="1" stop-color="color-mix(in oklab, var(--sj-collar) 50%, black)" />
          </radialGradient>
          <radialGradient id="sj-collar-secondary-grad" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0.55" stop-color="color-mix(in oklab, var(--sj-collar-secondary) 70%, black)" />
            <stop offset="0.85" stop-color="var(--sj-collar-secondary)" />
            <stop offset="1" stop-color="color-mix(in oklab, var(--sj-collar-secondary) 50%, black)" />
          </radialGradient>
          <radialGradient id="sj-collar-tertiary-grad" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0.55" stop-color="color-mix(in oklab, var(--sj-collar-tertiary) 75%, black)" />
            <stop offset="0.85" stop-color="var(--sj-collar-tertiary)" />
            <stop offset="1" stop-color="color-mix(in oklab, var(--sj-collar-tertiary) 55%, black)" />
          </radialGradient>
          <!-- Tally-ring bloom. The outermost element of the stack;
               its blurred halo simulates the illuminated LED skirt at
               the very base of the CGP-500 monoknob, picked up by
               peripheral vision before the operator looks down. -->
          <filter id="sj-tally-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="2.6" />
          </filter>
          <!-- Matte rubber dome. Wide soft radial gradient, no
               specular highlight (cap is textured rubber, not
               polished glass). Gradient origin up-and-left of centre
               implies ambient light from the operator's upper-left
               viewpoint. -->
          <radialGradient id="sj-cap-grad" cx="0.4" cy="0.32" r="0.75">
            <stop offset="0" stop-color="color-mix(in oklab, var(--sj-cap) 80%, white)" />
            <stop offset="0.55" stop-color="var(--sj-cap)" />
            <stop offset="1" stop-color="color-mix(in oklab, var(--sj-cap) 60%, black)" />
          </radialGradient>
        </defs>

        <!-- Lever-knob assembly: tally ring + collars + cap +
             master-black tick. Wrapped in a single group so the press
             animation moves the whole assembly as one unit (the entire
             joystick "sinks" into the chassis on press, not just the
             cap). Pointer handlers attached here so drag and
             right-click both target the full assembly. -->
        <g
          class="lever-knob"
          style="transform-origin: ${capCx}px ${capCy}px;"
          @pointerdown=${this.#onPointerDown}
          @pointermove=${this.#onPointerMove}
          @pointerup=${this.#onPointerUp}
          @pointercancel=${this.#onPointerUp}
          @contextmenu=${this.#onContextMenu}
        >
          ${this.hasTallyRing
            ? html`
                <!-- Illuminated tally ring at the very base of the
                     stack. Drawn first so every collar and the cap sit
                     on top; only the outermost annulus is visible.
                     Bloomy halo behind a sharper ring carries the
                     coloured emission outward into the surrounding
                     chassis recess. -->
                <circle
                  class="tally-glow"
                  cx=${capCx}
                  cy=${capCy}
                  r="78"
                />
                <circle
                  class="tally-ring"
                  cx=${capCx}
                  cy=${capCy}
                  r="72"
                />
              `
            : ''}

          ${this.hasSecondaryRing
            ? html`
                <!-- Secondary collar (CGP-500 only) — wider than the
                     primary so it reads as the lower purple ring of
                     the stack (Var ColTmp on CGP-500). OCP-400 does
                     not set this. -->
                <circle
                  cx=${capCx}
                  cy=${capCy}
                  r=${collarR + 4}
                  fill="url(#sj-collar-secondary-grad)"
                />
                ${this.#renderDetents(capCx, capCy, collarR + 4, this.secondaryRingDetents, 1.2)}
              `
            : ''}

          <!-- Primary collar: knurled rotation ring at the cap base
               (Master Black on CGP-500 and OCP-400). Drawn before the
               cap so the cap covers its centre — only the outer
               annulus is visible, reading as the knurled ring around
               the cap. -->
          <circle
            cx=${capCx}
            cy=${capCy}
            r=${collarR}
            fill="url(#sj-collar-grad)"
          />
          ${this.#renderDetents(capCx, capCy, collarR, detentCount, 1.5)}

          ${this.hasTertiaryRing
            ? html`
                <!-- Tertiary collar (CGP-500 — Master Gain). Smaller
                     than the primary so its visible annulus sits just
                     outside the cap edge, between the cap and the
                     primary collar's outer edge. Reads as the upper
                     purple ring of the stack. -->
                <circle
                  cx=${capCx}
                  cy=${capCy}
                  r=${capR + 3}
                  fill="url(#sj-collar-tertiary-grad)"
                />
                ${this.#renderDetents(capCx, capCy, capR + 3, this.tertiaryRingDetents, 1)}
              `
            : ''}

          <!-- Cap: rubber dome on top. Radial gradient suggests the
               domed surface; the off-centre highlight hints at light
               coming from upper-left (operator perspective). Drawn
               last so it covers the collar centre. -->
          <circle
            cx=${capCx}
            cy=${capCy}
            r=${capR}
            fill="url(#sj-cap-grad)"
          />
          <!-- Faint outer rim shadow only. No specular highlight —
               broadcast rubber knobs are matte; the radial gradient
               on the cap alone carries the dome shading. -->
          <circle
            cx=${capCx}
            cy=${capCy}
            r=${capR}
            fill="none"
            stroke="color-mix(in oklab, var(--sj-cap) 50%, black)"
            stroke-width="0.5"
            opacity="0.4"
          />
          <!-- Master-black rotation indicator: a short radial tick
               fully contained within the visible collar annulus
               (between the cap edge at r=capR and the collar's
               outer edge at r=collarR). The tick starts 1 vbu
               outside the cap edge and ends 1 vbu inside the
               collar's outer edge — it neither protrudes past the
               collar nor sits on the cap. Styled to match the
               rotary-encoder pointer (grey via --control-pointer,
               rounded ends) because the master-black collar is
               conceptually the same control class as a variable
               potentiometer. -->
          <line
            x1=${capCx}
            y1=${capCy - (collarR - 1)}
            x2=${capCx}
            y2=${capCy - (capR + 1)}
            stroke="var(--sj-pointer)"
            stroke-width="2"
            stroke-linecap="round"
            transform="rotate(${masterBlackAngleDeg} ${capCx} ${capCy})"
            opacity="0.9"
          />
        </g>

      </svg>
    `;
  }

  /**
   * Render N tick marks evenly spaced around a ring. Each tick is a
   * short radial line pointing inward from the ring's circumference.
   * Used to indicate the number of tactile detents on a rotation
   * collar.
   */
  #renderDetents(cx: number, cy: number, ringR: number, count: number, tickLength: number) {
    if (count <= 0) return '';
    const ticks = [];
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * 2 * Math.PI;
      const outerX = cx + Math.cos(angle) * ringR;
      const outerY = cy + Math.sin(angle) * ringR;
      const innerX = cx + Math.cos(angle) * (ringR - tickLength);
      const innerY = cy + Math.sin(angle) * (ringR - tickLength);
      ticks.push(html`
        <line
          x1=${outerX}
          y1=${outerY}
          x2=${innerX}
          y2=${innerY}
          stroke="var(--sj-detent)"
          stroke-width="0.35"
          opacity="0.85"
        />
      `);
    }
    return ticks;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'shading-joystick': ShadingJoystickElement;
  }
}
